import { makeStyles } from '@mui/styles';
import { Box, IconButton, InputAdornment, TextField, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import moment from 'moment';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import KenIcon from '../KenIcon';
import ErrorIcon from '../../Assets/Icons/error.png';

const useStyles = makeStyles({
  root: {
    width: '100%',
    '&>:nth-child(1)': {
      backgroundColor: '#F3F5F7',
      color: '#000 !important',
    },
  },
  rootCircular: {
    width: '100%',
    '&>:nth-child(1)': {
      padding: 0,
    },
  },
  input: {
    borderRadius: '4px',
    padding: '16px',
    backgroundColor: '#F3F5F7',
    color: '#000 !important',
    textAlign: 'left',
    '&:focus': {
      backgroundColor: '#FFFFFF',
    },
  },
  error: {
    backgroundColor: '#FFFFFF',
  },
  helperText: {
    marginLeft: 0,
    fontSize: '12px',
    color: '#FC2C32',
  },
});

const StyledTextField = styled(TextField)(({ error, border }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fff',
    height: "auto",
    fontSize: "14px",
    '& fieldset': {
      border: '0.5px solid #B0B0B0',
      color: '#000 !important',
    },
    '&:hover fieldset': {
      borderColor: error ? '#FC2C32' : 'grey',
    },
    '&.Mui-focused fieldset': {
      borderColor: error ? '#FC2C32' : '#092682',
    },
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: '#FC2C32',
  },
  '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-error.MuiOutlinedInput-notchedOutline': {
    borderColor: '#FC2C32',
  },
  '& .MuiFormControl-root-MuiTextField-root .MuiOutlinedInput-root': {
    backgroundColor: '#fff',
  },
  '& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input': {
    color: '#000 !important',
  },
  '& .css-ahj2mt-MuiTypography-root': {
    'font-family': '"GeneralSans" !important',
  },
  '& .css-1wc848c-MuiFormHelperText-root': {
    'margin-left': '0px !important',
  },
}));

const StyledTextFieldCircular = styled(TextField)(({ error, border }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#F3F5F7',
    },
    '&:hover fieldset': {
      borderColor: error ? '#FC2C32' : 'grey',
    },
    '&.Mui-focused fieldset': {
      borderColor: error ? '#FC2C32' : '#092682',
    },
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: '#FC2C32',
  },
  '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-error.MuiOutlinedInput-notchedOutline': {
    borderColor: '#FC2C32',
  },
  '& .MuiFormControl-root-MuiTextField-root .MuiOutlinedInput-root': {
    backgroundColor: '#F3F5F7',
  },
  '& .MuiInputBase-adornedEnd': {
    backgroundColor: '#f3f5f7',
  },
}));

export default function KenTextField(props) {
  const {
    FormHelperTextProps,
    InputLabelProps,
    inputProps,
    InputProps,
    className,
    error,
    errorLabel,
    children,
    disableKeyDown,
    minDate,
    maxDate,
    name,
    value,
    remaining = false,
    maxCharacters,
    inputType,
    dateType = 'year',
    withValue,
    typeOfInput,
    handleMouseDownPassword,
    showPassword,
    multiple = false,
    onTeamSizeChange,
    onChange,
  } = props;

  const classes = useStyles();
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const renderInput = (flag) => {
    switch (flag) {
      case 'circular':
        return (
          <StyledTextFieldCircular
            className={`${className}`}
            classes={{ root: classes.rootCircular }}
            InputLabelProps={{ shrink: false, ...InputLabelProps }}
            inputProps={{
              className: error ? `${classes.input} ${classes.error}` : classes.input,
              ...inputProps,
            }}
            InputProps={{
              components: 'p',
              endAdornment:
                typeOfInput === 'password' ? (
                  <InputAdornment position="center" classes={{ positionStart: '0px' }}>
                    <IconButton
                      // onClick={handleMouseDownPassword}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ) : null,
              inputProps: {
                min:
                  minDate &&
                  minDate !== null &&
                  (minDate === true
                    ? moment().format('YYYY-MM-DD')
                    : withValue
                    ? moment(value).subtract(Number(minDate), dateType).format('YYYY-MM-DD')
                    : moment().subtract(Number(minDate), dateType).format('YYYY-MM-DD')),
                max:
                  maxDate &&
                  maxDate !== null &&
                  (maxDate === true
                    ? moment().format('YYYY-MM-DD')
                    : withValue
                    ? moment(value).add(Number(maxDate), dateType).format('YYYY-MM-DD')
                    : moment().add(Number(maxDate), dateType).format('YYYY-MM-DD')),
                'data-testid': `test-${name}`,
              },
              ...InputProps,
            }}
            FormHelperTextProps={{
              className: classes.helperText,
              ...FormHelperTextProps,
            }}
            placeholder={`Enter ${name}`}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: '300px',
                    marginTop: '8px',
                    boxSizing: 'border-box',
                    borderTop: '8px solid #ffffff',
                    borderBottom: '8px solid #ffffff',
                  },
                },
              },
              multiple: multiple,
            }}
            {...props}
            multiSelect={true}
            onKeyDown={(e) => {
              disableKeyDown && e.preventDefault();
            }}
            onChange={(e) => {
              if (onTeamSizeChange) {
                onTeamSizeChange(e);
              }
              if (onChange) {
                onChange(e);
              }
            }}
            label=""
            error={errorLabel ? errorLabel : error}
            helperText={
              errorLabel ? null : error ? (
                <>
                  <Box height="16px" margin="19px 8px" alignItems={'flex-start'} display={'flex'}>
                    <KenIcon iconType="img" icon={ErrorIcon} variant="extraSmall"></KenIcon> &nbsp;&nbsp;
                    {error}
                  </Box>
                </>
              ) : (
                <>{remaining === true && <p style={{ color: '#505F79', marginTop: '-2px' }}>Remaining Characters: {maxCharacters - (value?.length || 0)}</p>}</>
              )
            }
          >
            {children}
          </StyledTextFieldCircular>
        );

      default:
        return (
          <StyledTextField
            className={`${className}`}
            classes={{ root: classes.root }}
            InputLabelProps={{ shrink: false, ...InputLabelProps }}
            inputProps={{
              className: error ? `${classes.input} ${classes.error}` : classes.input,
              ...inputProps,
            }}
            InputProps={{
              components: 'p',
              inputProps: {
                min:
                  minDate &&
                  minDate !== null &&
                  (minDate === true
                    ? moment().format('YYYY-MM-DD')
                    : withValue
                    ? moment(value).subtract(Number(minDate), dateType).format('YYYY-MM-DD')
                    : moment().subtract(Number(minDate), dateType).format('YYYY-MM-DD')),
                max:
                  maxDate &&
                  maxDate !== null &&
                  (maxDate === true
                    ? moment().format('YYYY-MM-DD')
                    : withValue
                    ? moment(value).add(Number(maxDate), dateType).format('YYYY-MM-DD')
                    : moment().add(Number(maxDate), dateType).format('YYYY-MM-DD')),
                'data-testid': `test-${name}`,
              },
              ...InputProps,
            }}
            FormHelperTextProps={{
              className: classes.helperText,
              ...FormHelperTextProps,
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: '300px',
                    marginTop: '8px',
                    boxSizing: 'border-box',
                    borderTop: '8px solid #ffffff',
                    borderBottom: '8px solid #ffffff',
                  },
                },
              },
              multiple: multiple,
            }}
            {...props}
            placeholder={isMobileScreen ? props?.label : props?.placeholder}
            multiSelect={true}
            onKeyDown={(e) => {
              disableKeyDown && e.preventDefault();
            }}
            onChange={(e) => {
              if (onTeamSizeChange) {
                onTeamSizeChange(e);
              }
              if (onChange) {
                onChange(e);
              }
            }}
            label=""
            error={errorLabel ? errorLabel : error}
            helperText={
              errorLabel ? null : error ? (
                <>
                  <Box height="16px" margin="8px 0px" alignItems={'flex-start'} display={'flex'}>
                    <KenIcon iconType="img" icon={ErrorIcon} variant="extraSmall"></KenIcon> &nbsp;&nbsp;
                    {error}
                  </Box>
                </>
              ) : (
                <>{remaining === true && <p style={{ color: '#505F79', marginTop: '-2px' }}>Remaining Characters: {maxCharacters - (value?.length || 0)}</p>}</>
              )
            }
          >
            {children}
          </StyledTextField>
        );
    }
  };

  return <>{renderInput(inputType)}</>;
}
